// Words for U3 "Start easily", pure so tests pin the clock and the zone: the ask line under a
// card's title ("Sam asks you to confirm Thursday · by Wednesday (“by Wed EOD”)"), when a
// deadline is, where focus stopped ("You stopped at 4 of 9 yesterday afternoon"), and a focus
// time offer ("tomorrow at 10"). Times read in the owner's zone, relative where natural; calm
// words only ("waiting for you", never "overdue").
import type { CardSummary } from "@shared/api";
import {
  focusCountWords,
  hasAsk,
  minutesWords,
  roundEstimate,
  shortMinutesWords,
  type CardAsk,
  type FocusPosition,
  type RoundEstimate,
} from "@shared/startEasily";
import { isToReview } from "./selectors";

const DAY_MS = 86_400_000;

interface Local {
  /** Days since the epoch for the local calendar date (for day differences). */
  dayNo: number;
  hour: number;
  minute: number;
  weekday: string;
  monthDay: string;
}

function local(ms: number, timeZone: string | undefined): Local {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
    weekday: "long",
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const dayNo =
    Date.UTC(Number(get("year")), Number(get("month")) - 1, Number(get("day"))) / DAY_MS;
  const monthDay = new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
  }).format(new Date(ms));
  return {
    dayNo,
    hour: Number(get("hour")) % 24,
    minute: Number(get("minute")),
    weekday: get("weekday"),
    monthDay,
  };
}

/** "10:00 AM" style clock words, "10 AM" on the hour. */
function clock(l: Pick<Local, "hour" | "minute">): string {
  const h = l.hour % 12 || 12;
  const ampm = l.hour < 12 ? "AM" : "PM";
  return l.minute ? `${h}:${String(l.minute).padStart(2, "0")} ${ampm}` : `${h} ${ampm}`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "today", "tomorrow", "Wednesday" (this week), else "Oct 30". */
function dayWords(diff: number, l: Local): string {
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";
  if (diff > 1 && diff < 7) return l.weekday;
  if (diff < -1 && diff > -7) return l.weekday;
  return l.monthDay;
}

/**
 * When a deadline is, relative to now in the owner's zone: "by today", "by 5 PM tomorrow", "by
 * Wednesday", "by Oct 30". One that has passed reads "asked for Monday" (never "overdue").
 * Null when the date can't be read.
 */
export function deadlineWords(at: string, now: number, timeZone?: string): string | null {
  const dateOnly = DATE_ONLY.test(at.trim());
  const ms = dateOnly ? Date.parse(`${at.trim()}T12:00:00Z`) : Date.parse(at);
  if (Number.isNaN(ms)) return null;
  const today = local(now, timeZone);
  // A date-only deadline is that calendar day wherever the owner is.
  const when = dateOnly ? local(ms, "UTC") : local(ms, timeZone);
  const diff = when.dayNo - today.dayNo;
  const day = dayWords(diff, when);
  const endOfDay = when.hour === 0 && when.minute === 0;
  const timed = !dateOnly && !endOfDay && !(when.hour >= 23 && when.minute >= 59);
  if (diff < 0 || (diff === 0 && !dateOnly && ms < now)) {
    return diff === 0 ? "asked for earlier today" : `asked for ${day}`;
  }
  if (!timed) return `by ${day}`;
  return diff === 0 ? `by ${clock(when)} today` : `by ${clock(when)} ${day}`;
}

export interface AskLineWords {
  text: string | null;
  when: string | null;
  quote: string | null;
}

/** The ask line's parts, or null when there is no ask (the line is hidden). */
export function askLineWords(
  ask: CardAsk | null | undefined,
  now: number,
  timeZone?: string,
): AskLineWords | null {
  if (!ask || !hasAsk(ask)) return null;
  const text = ask.text?.trim() || null;
  const when = ask.deadline ? deadlineWords(ask.deadline.at, now, timeZone) : null;
  const rawQuote = ask.deadline?.quote.trim() ?? "";
  // The email's own words, unless they say nothing more than the date words already do.
  const quote =
    rawQuote && when && !when.toLowerCase().includes(rawQuote.toLowerCase()) ? rawQuote : null;
  if (!text && !when) return null;
  return { text, when, quote };
}

/** One plain string for the ask line (screen readers and tests). */
export function askLineText(words: AskLineWords): string {
  const deadline = words.when ? `${words.when}${words.quote ? ` (“${words.quote}”)` : ""}` : "";
  return [words.text, deadline].filter(Boolean).join(" · ");
}

function dayPart(hour: number): "morning" | "afternoon" | "evening" | "night" {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "night";
}

/** "a few minutes ago", "this morning", "yesterday afternoon", "last night", "on Monday evening". */
export function whenStoppedWords(at: string, now: number, timeZone?: string): string {
  const ms = Date.parse(at);
  if (Number.isNaN(ms)) return "earlier";
  const ago = now - ms;
  if (ago < 15 * 60_000) return "a few minutes ago";
  if (ago < 60 * 60_000) return `${Math.round(ago / 60_000 / 5) * 5} minutes ago`;
  const then = local(ms, timeZone);
  const diff = local(now, timeZone).dayNo - then.dayNo;
  const part = dayPart(then.hour);
  if (diff <= 0) return part === "night" ? "earlier tonight" : `this ${part}`;
  if (diff === 1) return part === "night" ? "last night" : `yesterday ${part}`;
  if (diff < 7) return `on ${then.weekday} ${part}`;
  return `on ${then.monthDay}`;
}

/** "You stopped at 4 of 9 yesterday afternoon." */
export function stoppedAtWords(position: FocusPosition, now: number, timeZone?: string): string {
  return `You stopped at ${position.index + 1} of ${position.total} ${whenStoppedWords(position.at, now, timeZone)}.`;
}

/** How long after stopping the pick-up offer appears (a quick hop to the feed and back is not "left"). */
export const PICK_UP_AFTER_MS = 10 * 60_000;

/**
 * Whether to offer "Pick up?": a position saved in the current round, past the first step, a
 * while ago, with that step still in the round.
 */
export function shouldOfferPickUp(
  position: FocusPosition | null | undefined,
  currentPass: number | null,
  now: number,
): position is FocusPosition {
  if (!position || currentPass === null || position.passNo !== currentPass) return false;
  if (position.index < 1 || position.index >= position.total) return false;
  return now - Date.parse(position.at) >= PICK_UP_AFTER_MS;
}

/** "tomorrow at 10 AM", "today at 2:30 PM", "Friday at 9 AM". */
export function focusSlotWords(start: string, now: number, timeZone?: string): string {
  const ms = Date.parse(start);
  if (Number.isNaN(ms)) return "soon";
  const when = local(ms, timeZone);
  const diff = when.dayNo - local(now, timeZone).dayNo;
  return `${dayWords(diff, when)} at ${clock(when)}`;
}

/** The open cards of a round, estimated; a suggested cleanup counts as a quick step. */
export function estimateOf(cards: readonly CardSummary[], cleanups = 0): RoundEstimate {
  const quick = Array.from({ length: Math.max(0, cleanups) }, () => "quick" as const);
  return roundEstimate([...cards.filter(isToReview).map((c) => c.effort), ...quick]);
}

/** "about 12 minutes · 1 needs focus time" (null when estimates are hidden or nothing is left). */
export function roundEstimateWords(estimate: RoundEstimate): string | null {
  const parts = [minutesWords(estimate.minutes), focusCountWords(estimate.longCount)];
  const text = parts.filter(Boolean).join(" · ");
  return text || null;
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Words for the offer's two buttons; pure so tests can read them. */
export function nextRoundOfferWords(
  estimate: RoundEstimate,
  showEstimates: boolean,
): { start: string; quick: string | null } {
  const minutes = showEstimates ? minutesWords(estimate.minutes) : null;
  const start = minutes ? `${capital(minutes)} · Start` : "Next round ready";
  const someQuick = estimate.quickCount > 0 && estimate.quickCount < estimate.count;
  if (!someQuick) return { start, quick: null };
  const quickMinutes = showEstimates ? shortMinutesWords(estimate.quickMinutes) : null;
  return {
    start,
    quick: quickMinutes ? `Just the quick ones (${quickMinutes})` : "Just the quick ones",
  };
}
